import React from "react";
import { motion } from "framer-motion";
import { Crown, Cpu, BarChart3, Check } from "lucide-react";
import { SectionHeader } from "@/components/sections/Services";
import { HOME } from "@/constants/testIds";

const features = [
  {
    icon: Crown,
    title: "Premium",
    subtitle: "Demand",
    text:
      "Access to high-quality advertisers and premium demand sources that maximize your revenue potential with competitive eCPMs and consistent fill rates.",
    bullets: [
      "Fortune 500 advertisers",
      "Competitive eCPMs",
      "Brand safety guaranteed",
    ],
  },
  {
    icon: Cpu,
    title: "Advanced",
    subtitle: "Tech",
    text:
      "Cutting-edge ad serving technology with real-time optimization, header bidding, and advanced targeting capabilities for maximum performance.",
    bullets: [
      "Header bidding integration",
      "Real-time optimization",
      "Ad quality control",
    ],
  },
  {
    icon: BarChart3,
    title: "Data Driven",
    subtitle: "Approach",
    text:
      "Comprehensive analytics and insights to optimize your ad performance with actionable data, detailed reporting, and revenue forecasting.",
    bullets: [
      "Real-time analytics",
      "Revenue forecasting",
      "User behavior insights",
    ],
  },
];

const pubStats = [
  { v: "98%", label: "Average Fill Rate" },
  { v: "$4.50", label: "Average eCPM" },
  { v: "24/7", label: "Support Available" },
  { v: "60 Days", label: "Payment Terms" },
];

export default function Publishers() {
  return (
    <section
      id="publishers"
      data-testid={HOME.publishersSection}
      className="relative py-32"
    >
      <div className="aurora-orb bg-[#00F0FF] w-[500px] h-[500px] top-20 -right-40 opacity-25" />
      <div className="container-x relative">
        <SectionHeader
          eyebrow="For Publishers"
          title={
            <>
              Maximize
              <br />
              <span className="aurora-text">Your Revenue Potential</span>
            </>
          }
          subtitle="Advanced technology and premium demand sources to help you monetize your content effectively with industry-leading fill rates and eCPMs."
        />

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="tile p-8 h-full"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0047FF]/30 to-[#8A2BE2]/20 border border-white/10 grid place-items-center mb-5">
                <f.icon className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <h3 className="font-display text-2xl font-bold mb-2">
                {f.title}
                <span className="block text-base text-white/50 font-normal">
                  {f.subtitle}
                </span>
              </h3>
              <p className="text-sm text-white/65 mb-5">{f.text}</p>
              <ul className="space-y-2">
                {f.bullets.map((b) => (
                  <li
                    key={b}
                    className="text-sm text-white/75 flex items-start gap-2"
                  >
                    <Check className="w-4 h-4 text-[#00F0FF] mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="glass-strong rounded-3xl p-10"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-3">
            Why Publishers Choose
          </div>
          <h3 className="font-display text-3xl md:text-4xl font-bold mb-10">
            Monetise<span className="cyan-text">UP</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {pubStats.map((s) => (
              <div key={s.label}>
                <div className="font-display text-4xl md:text-5xl font-bold mb-2">
                  {s.v}
                </div>
                <div className="text-sm text-white/55">{s.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
